/**
 * resolveEffectiveConclusion — render-time and gen-time conclusion resolver.
 *
 * Typed feasibility reports omit `conclusion` from FEASIBILITY_SCHEMA, so
 * `report.conclusion` is often undefined even when categories/overallScore
 * are populated. This module normalizes stored conclusions and synthesizes
 * the deterministic feasibility conclusion using the same rules as the
 * legacy `generateReport` path in siteRagService.ts.
 */
import type { SiteRagReport, RagCategory, Recommendation } from '../types/siteRag';
import { SDC_ORDER } from '../services/dataProviders/usgsSeismic';
import {
  buildCustomBriefSynthesisConclusion,
  buildExecutiveSummarySynthesisConclusion,
  isThinSynthesisConclusion,
} from './buildSynthesisConclusion';

type Conclusion = NonNullable<SiteRagReport['conclusion']>;
type RecommendationVerdict = Conclusion['recommendation'];

const SAFETY_CRITICAL_METRICS = ['seismic_risk', 'flood_risk'] as const;

function sdcAtLeast(sdc: string, threshold: string): boolean {
  return (SDC_ORDER[sdc] ?? 0) >= (SDC_ORDER[threshold] ?? 0);
}

function isValidConclusionObject(raw: unknown): raw is Conclusion {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const rec = (raw as { recommendation?: unknown }).recommendation;
  return typeof rec === 'string' && rec.trim().length > 0;
}

function inferRecommendationFromText(text: string): RecommendationVerdict | undefined {
  const lower = text.toLowerCase();
  // P1.4 — a withheld / pending headline must not fall through to the
  // "Proceed with Conditions" default below.
  if (/decision pending|recommendation withheld|verdict withheld/.test(lower)) return 'Decision Pending';
  if (lower.includes('do not proceed')) return 'Do Not Proceed';
  if (lower.includes('proceed with conditions')) return 'Proceed with Conditions';
  if (/\bproceed\b/.test(lower)) return 'Proceed';
  return undefined;
}

function normalizeStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((s): s is string => typeof s === 'string')
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

/** Normalize a Gemini or persisted conclusion value into the report shape. */
export function normalizeStoredConclusion(
  raw: unknown,
  overallScore: number,
): Conclusion | undefined {
  if (raw === null || raw === undefined) return undefined;

  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return undefined;
    const recommendation = inferRecommendationFromText(text) ?? 'Proceed with Conditions';
    return {
      recommendation,
      overallScore: Math.round(overallScore),
      topStrengths: [],
      topRisks: [],
      keyConditions: [],
      nextActions: [],
      environmentalVerdict: text,
    };
  }

  if (!isValidConclusionObject(raw)) return undefined;

  const obj = raw as Record<string, unknown>;
  return {
    recommendation: String(obj.recommendation).trim() as RecommendationVerdict,
    overallScore: typeof obj.overallScore === 'number' && Number.isFinite(obj.overallScore)
      ? Math.round(obj.overallScore)
      : Math.round(overallScore),
    topStrengths: normalizeStringArray(obj.topStrengths),
    topRisks: normalizeStringArray(obj.topRisks),
    topConstraints: normalizeStringArray(obj.topConstraints),
    keyOpportunities: normalizeStringArray(obj.keyOpportunities),
    keyConditions: normalizeStringArray(obj.keyConditions),
    nextActions: normalizeStringArray(obj.nextActions),
    ...(typeof obj.environmentalVerdict === 'string' && obj.environmentalVerdict.trim()
      ? { environmentalVerdict: obj.environmentalVerdict.trim() }
      : {}),
    ...(typeof obj.btmDecisionSummary === 'string' && obj.btmDecisionSummary.trim()
      ? { btmDecisionSummary: obj.btmDecisionSummary.trim() }
      : {}),
    ...(typeof obj.insightModifier === 'number' && Number.isFinite(obj.insightModifier)
      ? { insightModifier: obj.insightModifier as number }
      : {}),
    ...(typeof obj.modifierJustification === 'string' && obj.modifierJustification.trim()
      ? { modifierJustification: obj.modifierJustification.trim() }
      : {}),
  };
}

export interface FeasibilityConclusionInput {
  overallScore: number;
  categories: RagCategory[];
  dataQuality?: SiteRagReport['dataQuality'];
  criticalDataGaps?: string[];
  risks?: Array<{ label?: string; impact?: string }>;
  recommendations?: Array<Pick<Recommendation, 'title' | 'description'>>;
  climate?: { waterStress?: string };
  seismic?: { designCategory?: string };
  infrastructure?: { fiberDensity?: string };
  grid?: { substationDist?: number | string };
}

function computeFeasibilityRecommendation(
  overallScore: number,
  categories: RagCategory[],
  dataQuality: SiteRagReport['dataQuality'] | undefined,
): RecommendationVerdict {
  const redCategories = categories.filter(c => c.status === 'red');
  const criticalFailures = categories.flatMap(c => c.metrics ?? [])
    .filter(m => SAFETY_CRITICAL_METRICS.includes(m.id as typeof SAFETY_CRITICAL_METRICS[number]) && m.score < 25);
  const hasCriticalFailure = criticalFailures.length > 0;
  const isDegraded = dataQuality === 'degraded';

  if (isDegraded) {
    return overallScore >= 45 ? 'Proceed with Conditions' : 'Do Not Proceed';
  }
  if (hasCriticalFailure) {
    return overallScore >= 45 ? 'Proceed with Conditions' : 'Do Not Proceed';
  }
  if (overallScore >= 70) return 'Proceed';
  if (overallScore >= 45 && redCategories.length === 0) return 'Proceed with Conditions';
  return 'Do Not Proceed';
}

function parseSubstationDistKm(grid: FeasibilityConclusionInput['grid']): number | undefined {
  const raw = grid?.substationDist;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const parsed = parseFloat(raw.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** Build the deterministic feasibility Go/No-Go conclusion from scoring inputs. */
export function buildDeterministicFeasibilityConclusion(
  input: FeasibilityConclusionInput,
): Conclusion {
  const categoriesArr = input.categories;
  const overallScore = input.overallScore;
  const sortedCats = [...categoriesArr].sort((a, b) => b.score - a.score);
  const allMetrics = categoriesArr.flatMap(c => (c.metrics ?? []).map(m => ({ ...m, category: c.label })));
  const sortedMetrics = [...allMetrics].sort((a, b) => b.score - a.score);
  const topStrengths = sortedMetrics.slice(0, 3).map(m => `${m.label}: ${m.explanation} (${m.score}/100)`);
  const topRisks = sortedMetrics.slice(-3).reverse().map(m => `${m.label}: ${m.explanation} (${m.score}/100)`);
  const redCategories = categoriesArr.filter(c => c.status === 'red');
  const criticalFailures = categoriesArr.flatMap(c => c.metrics ?? [])
    .filter(m => SAFETY_CRITICAL_METRICS.includes(m.id as typeof SAFETY_CRITICAL_METRICS[number]) && m.score < 25);
  const recommendation = computeFeasibilityRecommendation(overallScore, categoriesArr, input.dataQuality);
  const isDegraded = input.dataQuality === 'degraded';
  const criticalDataGaps = input.criticalDataGaps ?? [];
  const risks = input.risks ?? [];
  const recommendations = input.recommendations ?? [];
  const climate = input.climate;
  const seismic = input.seismic;
  const infrastructure = input.infrastructure;
  const substationDist = parseSubstationDistKm(input.grid);

  return {
    recommendation,
    overallScore: Math.round(overallScore),
    topStrengths,
    topRisks,
    keyConditions: [
      ...criticalDataGaps.map(g => `DATA GAP: ${g}. Update via Evidence Data and regenerate.`),
      ...(isDegraded ? ['DATA QUALITY: Multiple data providers failed — re-run assessment when APIs are available'] : []),
      ...criticalFailures.map(m => `CRITICAL: ${m.label} — ${m.explanation} (score ${m.score})`),
      ...(recommendation === 'Proceed with Conditions'
        ? [
            ...sortedCats.filter(c => c.status === 'amber').map(c => `${c.label}: requires mitigation (score ${c.score})`),
            ...sortedCats
              .filter(c => c.status === 'green')
              .flatMap(c => {
                const hasRedWaterStress = (c.metrics ?? []).some(
                  m => m.status === 'red' || (m.id === 'water_stress' && /high|extreme/i.test(String(m.value ?? ''))),
                );
                const hasMissingZoning = (c.metrics ?? []).some(m => m.id === 'zoning' && m.missing);
                if (c.id === 'cooling' && hasRedWaterStress) {
                  return [`${c.label}: verify physical water supply/rights and closed-loop dry/adiabatic thermal design (score ${c.score} climate-weighted proxy; WRI water stress High)`];
                }
                if (c.id === 'regulatory' && hasMissingZoning) {
                  return [`${c.label}: confirm local zoning designation, on-site generation land-use approval, and air-permit applicability (score ${c.score} state-policy proxy; local zoning unverified)`];
                }
                return [];
              }),
          ]
        : recommendation === 'Do Not Proceed'
          ? redCategories.map(c => `${c.label}: critical gap (score ${c.score})`)
          : []),
    ],
    nextActions: [
      risks[0]?.label
        ? `Address primary risk: ${risks[0].label} — ${risks[0].impact || 'assess impact'}`
        : 'Commission detailed site survey',
      (recommendations[0]?.title && recommendations[0]?.description)
        ? `${recommendations[0].title}: ${recommendations[0].description}`
        : 'Engage local utility for interconnection study',
      climate?.waterStress === 'High'
        ? 'Commission water sourcing study — high stress area limits cooling options'
        : (typeof seismic?.designCategory === 'string' && sdcAtLeast(seismic.designCategory, 'C'))
          ? `Commission site-specific PSHA — SDC ${seismic.designCategory} requires seismic characterization`
          : infrastructure?.fiberDensity === 'Low'
            ? 'Engage fiber providers for dark fiber route feasibility'
            : substationDist !== undefined && substationDist > 15
              ? 'Initiate utility interconnection pre-application for substation access'
              : 'Proceed to Phase 1 site characterization and environmental baseline',
    ],
  };
}

function buildFeasibilityConclusionFromReport(report: SiteRagReport): Conclusion | undefined {
  if (!report.categories || report.categories.length === 0) return undefined;
  return buildDeterministicFeasibilityConclusion({
    overallScore: report.overallScore,
    categories: report.categories,
    dataQuality: report.dataQuality,
    risks: report.risks,
    recommendations: report.recommendations,
    climate: report.datasets?.climate,
    seismic: report.datasets?.seismic,
    infrastructure: report.datasets?.infrastructure,
    grid: report.datasets?.grid,
  });
}

function buildSynthesisConclusionFromReport(report: SiteRagReport): Conclusion | undefined {
  const reportType = report.reportType ?? 'feasibility';
  if (reportType === 'custom-brief') {
    const existing = isValidConclusionObject(report.conclusion) ? report.conclusion : undefined;
    if (existing && !isThinSynthesisConclusion(existing)) return existing;
    const synth = buildCustomBriefSynthesisConclusion({
      structuredExec: report.customBriefExecutiveSummary,
      customBriefAnswers: report.customBriefAnswers ?? [],
      btmDecisionSummary: report.btmDecisionSummary ?? report.conclusion?.btmDecisionSummary,
      existing: report.customBriefConclusion,
    });
    return synth.conclusion;
  }
  if (reportType === 'executive-summary') {
    const existing = isValidConclusionObject(report.conclusion) ? report.conclusion : undefined;
    if (existing && !isThinSynthesisConclusion(existing)) return existing;
    const actionPlan = (report.recommendations ?? [])
      .map(r => r.title?.trim() ?? '')
      .filter(Boolean);
    return buildExecutiveSummarySynthesisConclusion({
      btmViabilityAssessment: report.btmViabilityAssessment ?? report.btmDecisionSummary,
      executiveSummaryCriticalRisks: report.executiveSummaryCriticalRisks,
      actionPlan: actionPlan.length > 0 ? actionPlan : undefined,
      keyFindings: (report.structuredFindings ?? []).map(f => ({
        title: f.title,
        detail: f.detail,
        impact: f.impact,
      })),
      stringConclusion: report.conclusion?.environmentalVerdict,
      existing: existing && !isThinSynthesisConclusion(existing) ? existing : undefined,
    });
  }
  return undefined;
}

/**
 * Resolve the conclusion to render or persist: passthrough valid stored
 * conclusions (including environmental), normalize string shapes, and
 * synthesize feasibility conclusions when missing.
 */
export function resolveEffectiveConclusion(
  report: SiteRagReport,
): Conclusion | undefined {
  const stored = (report as SiteRagReport & { conclusion?: unknown }).conclusion;
  const reportType = report.reportType ?? 'feasibility';

  if (isValidConclusionObject(stored)) {
    const typedStored = stored as Conclusion;
    if (
      reportType === 'feasibility'
      || reportType === 'environmental'
      || !isThinSynthesisConclusion(typedStored)
    ) {
      return typedStored;
    }
  }

  if (typeof stored === 'string') {
    const fromString = normalizeStoredConclusion(stored, report.overallScore);
    if (fromString) return fromString;
  }

  const normalized = normalizeStoredConclusion(stored, report.overallScore);
  if (normalized && !isThinSynthesisConclusion(normalized)) return normalized;

  if (reportType === 'feasibility') {
    return buildFeasibilityConclusionFromReport(report);
  }

  return buildSynthesisConclusionFromReport(report);
}

export interface CanonicalReportDecisionProjection {
  band: 'go' | 'conditional' | 'no-go' | 'withheld';
  statusBand: 'go' | 'conditional' | 'no-go' | 'withheld';
  conclusionBand?: 'go' | 'conditional' | 'no-go' | 'withheld';
  bindingConstraints: string[];
  rationale: string;
}

function statusToDecisionBand(
  status: SiteRagReport['overallStatus'] | string | undefined,
): 'go' | 'conditional' | 'no-go' | 'withheld' {
  const normalized = String(status ?? '').trim().toLowerCase();
  if (normalized === 'withheld') return 'withheld';
  if (normalized === 'red' || normalized === 'no_go' || normalized === 'no-go') return 'no-go';
  if (normalized === 'green' || normalized === 'go') return 'go';
  return 'conditional';
}

function recommendationToDecisionBand(
  recommendation: string | undefined,
): 'go' | 'conditional' | 'no-go' | 'withheld' | undefined {
  if (!recommendation || !recommendation.trim()) return undefined;
  const trimmed = recommendation.trim();
  const inferred = inferRecommendationFromText(trimmed);
  if (inferred === 'Decision Pending') return 'withheld';
  if (inferred === 'Do Not Proceed') return 'no-go';
  if (inferred === 'Proceed with Conditions') return 'conditional';
  if (inferred === 'Proceed') return 'go';
  const lower = trimmed.toLowerCase();
  if (/^(?:no[-\s]?go|challenging|unviable|unfinanceable|high\s+risk|decline|declined|stop)$/i.test(lower)) {
    return 'no-go';
  }
  if (/^(?:conditional|proceed\s+with\s+conditions|amber|moderate|caution)$/i.test(lower)) {
    return 'conditional';
  }
  if (/^(?:go|proceed|strong|viable|favorable|low\s+risk)$/i.test(lower)) {
    return 'go';
  }
  if (/^(?:withheld|decision\s+pending|pending|inconclusive)$/i.test(lower)) {
    return 'withheld';
  }
  return undefined;
}

const DECISION_BAND_SEVERITY: Record<'go' | 'conditional' | 'no-go' | 'withheld', number> = {
  go: 0,
  conditional: 1,
  'no-go': 2,
  withheld: 3,
};

/**
 * Single canonical decision & reason projection reconciling `overallScore` /
 * `overallStatus` (score tier), `conclusion.recommendation`, and deterministic
 * feasibility category/hazard constraints so semantic `systemDecision`,
 * report verdict, and portfolio composite never diverge.
 */
export function resolveCanonicalReportDecisionProjection(
  report: SiteRagReport,
): CanonicalReportDecisionProjection {
  const reportType = report.reportType ?? 'feasibility';
  const statusBand = statusToDecisionBand(report.overallStatus);
  const effectiveConclusion = resolveEffectiveConclusion(report);
  const rawRecommendation =
    report.conclusion?.recommendation?.trim() ||
    effectiveConclusion?.recommendation?.trim();
  const conclusionBand = recommendationToDecisionBand(rawRecommendation);

  let band: 'go' | 'conditional' | 'no-go' | 'withheld' = statusBand;
  if (
    conclusionBand !== undefined &&
    DECISION_BAND_SEVERITY[conclusionBand] > DECISION_BAND_SEVERITY[band]
  ) {
    band = conclusionBand;
  }

  const bindingConstraints: string[] = [];
  if (reportType === 'feasibility') {
    const categories = Array.isArray(report.categories) ? report.categories : [];
    const redCategories = categories.filter((c) => c.status === 'red');
    const criticalFailures = categories
      .flatMap((c) => c.metrics ?? [])
      .filter(
        (m) =>
          SAFETY_CRITICAL_METRICS.includes(m.id as (typeof SAFETY_CRITICAL_METRICS)[number]) &&
          typeof m.score === 'number' &&
          m.score < 25,
      );
    const keyConditions = report.conclusion?.keyConditions ?? effectiveConclusion?.keyConditions ?? [];
    const criticalConditionLines = keyConditions.filter((line) =>
      /^CRITICAL:\s+/i.test(String(line ?? '').trim()),
    );

    if (band === 'no-go' || redCategories.length > 0 || criticalFailures.length > 0 || criticalConditionLines.length > 0) {
      for (const cat of redCategories) {
        bindingConstraints.push(
          `${cat.label} (${cat.id}: ${Math.round(cat.score)}/100, status red)`,
        );
      }
      for (const metric of criticalFailures) {
        const label = `CRITICAL: ${metric.label}${metric.explanation ? `, ${metric.explanation}` : ''} (score ${Math.round(metric.score)}/100)`;
        if (!bindingConstraints.includes(label)) {
          bindingConstraints.push(label);
        }
      }
      for (const cond of criticalConditionLines) {
        const trimmed = cond.trim();
        if (
          trimmed &&
          !bindingConstraints.some((existing) =>
            existing.toLowerCase().includes(trimmed.toLowerCase().replace(/^critical:\s*/i, '').slice(0, 18)),
          )
        ) {
          bindingConstraints.push(trimmed);
        }
      }
      if (
        typeof report.overallScore === 'number' &&
        Number.isFinite(report.overallScore) &&
        report.overallScore < 45 &&
        band === 'no-go'
      ) {
        bindingConstraints.push(
          `Overall feasibility score ${Math.round(report.overallScore)}/100 (< 45 Do Not Proceed threshold)`,
        );
      }
    }
  }

  const baseRationale = report.conclusion?.recommendation?.trim() || '';
  const rationale =
    reportType === 'feasibility' &&
    band === 'no-go' &&
    statusBand !== 'no-go' &&
    bindingConstraints.length > 0
      ? `${baseRationale || 'Do Not Proceed'} — score ${Math.round(report.overallScore ?? 0)}/100 (${report.overallStatus ?? 'amber'} score tier) is capped at Do Not Proceed (no-go) by binding feasibility constraints: ${bindingConstraints.join('; ')}.`
      : band === 'no-go' && statusBand !== 'no-go'
        ? baseRationale || rawRecommendation || 'Do Not Proceed'
        : baseRationale;

  return {
    band,
    statusBand,
    ...(conclusionBand ? { conclusionBand } : {}),
    bindingConstraints,
    rationale,
  };
}
